import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Search,
    LogOut,
    Filter,
    Download,
    Trash2,
    FileText,
    Image as ImageIcon,
    Eye,
    Save,
    RefreshCw,
    Calendar,
    Phone,
    Mail,
    MapPin,
    Briefcase,
    Users,
    Send,
    AlertTriangle,
    KeyRound,
    CheckCircle2,
    XCircle,
    ClipboardList,
    FileDown,
} from "lucide-react";
import { apiClient, API, clearToken, getToken, formatApiError } from "../lib/api";
import { UserManagement } from "../components/admin/UserManagement";
import { EmailTemplates } from "../components/admin/EmailTemplates";
import { JobManagement } from "../components/admin/JobManagement";
import { HeroSettings } from "../components/admin/HeroSettings";
import { AdminStats } from "../components/admin/AdminStats";
import { LegalPages } from "../components/admin/LegalPages";
import { BenefitsSettings } from "../components/admin/BenefitsSettings";
import { GdprCard } from "../components/admin/GdprCard";
import { AboutEditor, WhatWeDoEditor, JobsIntroEditor } from "../components/admin/ContentEditors";
import { AnalyticsView } from "../components/admin/AnalyticsView";
import { SendEmailDialog } from "../components/admin/SendEmailDialog";
import { Star, Archive, Globe, BarChart3, Link as LinkIcon, RotateCcw } from "lucide-react";

const STATUSES = ["Neu", "In Prüfung", "Rückfrage", "Eingeladen", "Abgelehnt", "Eingestellt", "Zurückgezogen"];
const POSITIONS = ["Marketing & Organisation", "Bauhof Allrounder", "Elektriker / Anlagenmonteur", "Initiativbewerbung"];

const STATUS_COLORS = {
    Neu: "bg-blue-100 text-blue-800",
    "In Prüfung": "bg-amber-100 text-amber-800",
    Rückfrage: "bg-purple-100 text-purple-800",
    Eingeladen: "bg-emerald-100 text-emerald-800",
    Abgelehnt: "bg-red-100 text-red-800",
    Eingestellt: "bg-slate-800 text-white",
    Zurückgezogen: "bg-slate-200 text-slate-500 line-through",
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) +
            " " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

function isStale(a) {
    return a.status === "Neu" && Date.now() - new Date(a.created_at).getTime() > 7 * 24 * 3600 * 1000;
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [positionFilter, setPositionFilter] = useState("all");
    const [view, setView] = useState("active");
    const [sortBy, setSortBy] = useState("created_at");
    const [sortDir, setSortDir] = useState("desc");
    const [selected, setSelected] = useState(null);
    const [notes, setNotes] = useState("");
    const [statusEdit, setStatusEdit] = useState("");
    const [savingDetail, setSavingDetail] = useState(false);
    const [notifyApplicant, setNotifyApplicant] = useState(false);
    const [activeTab, setActiveTab] = useState("applications");
    const [websiteTab, setWebsiteTab] = useState("benefits");
    const [sendingTest, setSendingTest] = useState(false);
    const [positionOptions, setPositionOptions] = useState(POSITIONS);
    const [showPwDialog, setShowPwDialog] = useState(false);
    const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
    const [changingPw, setChangingPw] = useState(false);

    useEffect(() => {
        if (!getToken()) return;
        apiClient
            .get("/admin/jobs")
            .then(({ data }) => {
                const p = (data.items || []).map((j) => j.position);
                if (p.length) setPositionOptions(p);
            })
            .catch(() => {});
    }, []);

    const exportPdf = async () => {
        if (!selected) return;
        try {
            const res = await fetch(`${API}/applications/${selected.id}/export.pdf`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `bewerbung_${selected.last_name || "export"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("PDF konnte nicht erstellt werden.");
        }
    };

    const changePassword = async () => {
        if (pwForm.next.length < 8) {
            toast.error("Das neue Passwort muss mindestens 8 Zeichen haben.");
            return;
        }
        if (pwForm.next !== pwForm.confirm) {
            toast.error("Die Passwörter stimmen nicht überein.");
            return;
        }
        setChangingPw(true);
        try {
            await apiClient.post("/auth/change-password", {
                current_password: pwForm.current,
                new_password: pwForm.next,
            });
            toast.success("Passwort geändert.");
            setShowPwDialog(false);
            setPwForm({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Passwortänderung fehlgeschlagen.");
        } finally {
            setChangingPw(false);
        }
    };

    const sendTestEmail = async () => {
        setSendingTest(true);
        try {
            const { data } = await apiClient.post("/admin/test-email");
            if (data.result?.ok) {
                toast.success(`Test-E-Mail an ${data.sent_to} gesendet (ID: ${data.result.id}).`);
            } else {
                toast.error(`E-Mail-Fehler: ${data.result?.error || "Unbekannt"}`, { duration: 15000 });
            }
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Test-E-Mail fehlgeschlagen.");
        } finally {
            setSendingTest(false);
        }
    };

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                status: statusFilter,
                position: positionFilter,
                search,
                view,
                sort_by: sortBy,
                sort_dir: sortDir,
            };
            const { data } = await apiClient.get("/applications", { params });
            setItems(data.items || []);
        } catch (err) {
            if (err?.response?.status !== 401) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Bewerbungen konnten nicht geladen werden.");
            }
        } finally {
            setLoading(false);
        }
    }, [statusFilter, positionFilter, search, sortBy, sortDir, view]);

    useEffect(() => {
        document.title = "Bewerbungs-Dashboard · Möbus";
        if (!getToken()) {
            navigate("/admin");
            return;
        }
        fetchItems();
    }, [fetchItems, navigate]);

    const handleLogout = async () => {
        try {
            await apiClient.post("/auth/logout");
        } catch (e) {
            console.error("Logout request failed:", e);
        }
        clearToken();
        toast.success("Abgemeldet.");
        navigate("/admin");
    };

    const openDetail = async (id) => {
        try {
            const { data } = await apiClient.get(`/applications/${id}`);
            setSelected(data);
            setNotes(data.notes || "");
            setStatusEdit(data.status || "Neu");
            setNotifyApplicant(false);
        } catch (err) {
            toast.error("Detail konnte nicht geladen werden.");
        }
    };

    const closeDetail = () => {
        setSelected(null);
        setNotes("");
        setStatusEdit("");
        setNotifyApplicant(false);
    };

    const saveDetail = async () => {
        if (!selected) return;
        setSavingDetail(true);
        try {
            const { data } = await apiClient.patch(`/applications/${selected.id}`, {
                status: statusEdit,
                notes,
                notify_applicant: notifyApplicant,
            });
            if (notifyApplicant) {
                if (data.email_result?.ok) {
                    toast.success("Gespeichert – Bewerber wurde per E-Mail benachrichtigt.");
                } else {
                    toast.error(
                        `Gespeichert, aber E-Mail fehlgeschlagen: ${data.email_result?.error || "Unbekannter Fehler"}`,
                        { duration: 12000 }
                    );
                }
            } else {
                toast.success("Gespeichert.");
            }
            setSelected({ ...selected, status: statusEdit, notes });
            setNotifyApplicant(false);
            fetchItems();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Speichern fehlgeschlagen.");
        } finally {
            setSavingDetail(false);
        }
    };

    const deleteApp = async (id) => {
        if (!window.confirm("Diese Bewerbung wirklich löschen?")) return;
        try {
            await apiClient.delete(`/applications/${id}`);
            toast.success("Bewerbung gelöscht.");
            closeDetail();
            fetchItems();
        } catch (err) {
            toast.error("Löschen fehlgeschlagen.");
        }
    };

    const exportCsv = async () => {
        try {
            const token = getToken();
            const res = await fetch(`${API}/applications/export.csv`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("CSV-Export fehlgeschlagen");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `bewerbungen_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV exportiert.");
        } catch (err) {
            toast.error("Export fehlgeschlagen.");
        }
    };

    const downloadFile = async (appId, fileId, filename) => {
        try {
            const token = getToken();
            const res = await fetch(`${API}/applications/${appId}/files/${fileId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Download fehlgeschlagen");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Download fehlgeschlagen.");
        }
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("desc");
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Top bar */}
            <header className="bg-moebus-deep text-white sticky top-0 z-30">
                <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src="https://customer-assets.emergentagent.com/job_moebus-karriere/artifacts/15h4op5x_moebus_logo_fuer_wwww_kl%20Final%20Done-2.svg"
                            alt="Möbus"
                            className="h-11 w-auto object-contain"
                        />
                        <div className="leading-tight">
                            <div className="font-heading font-semibold text-sm md:text-base">
                                Möbus Karriere
                            </div>
                            <div className="text-[10px] md:text-xs uppercase tracking-widest text-emerald-400">
                                Admin · Bewerbungsverwaltung
                            </div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-1">
                        <button
                            data-testid="change-password-btn"
                            onClick={() => setShowPwDialog(true)}
                            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white px-3 py-2 rounded-md hover:bg-white/10"
                        >
                            <KeyRound size={16} /> Passwort
                        </button>
                        <button
                            data-testid="admin-logout"
                            onClick={handleLogout}
                            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white px-3 py-2 rounded-md hover:bg-white/10"
                        >
                            <LogOut size={16} /> Abmelden
                        </button>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 md:px-8 py-8">
                {/* Main Tabs */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                    <button
                        data-testid="tab-applications"
                        onClick={() => setActiveTab("applications")}
                        className={`inline-flex items-center gap-2.5 px-6 py-3 rounded-lg text-base font-semibold transition-all ${
                            activeTab === "applications"
                                ? "bg-moebus-deep text-white shadow-md"
                                : "bg-white text-slate-600 border border-moebus-border hover:bg-slate-50"
                        }`}
                    >
                        <Briefcase size={18} /> Bewerbungen
                    </button>
                    <button
                        data-testid="tab-jobs"
                        onClick={() => setActiveTab("jobs")}
                        className={`inline-flex items-center gap-2.5 px-6 py-3 rounded-lg text-base font-semibold transition-all ${
                            activeTab === "jobs"
                                ? "bg-moebus-deep text-white shadow-md"
                                : "bg-white text-slate-600 border border-moebus-border hover:bg-slate-50"
                        }`}
                    >
                        <ClipboardList size={18} /> Stellen
                    </button>
                    <button
                        data-testid="tab-stats"
                        onClick={() => setActiveTab("stats")}
                        className={`inline-flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ml-auto ${
                            activeTab === "stats"
                                ? "bg-moebus-deep text-white shadow-md"
                                : "bg-white text-slate-500 border border-moebus-border hover:bg-slate-50"
                        }`}
                    >
                        <BarChart3 size={16} /> Statistik
                    </button>
                    <button
                        data-testid="tab-website"
                        onClick={() => setActiveTab("website")}
                        className={`inline-flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                            activeTab === "website"
                                ? "bg-moebus-deep text-white shadow-md"
                                : "bg-white text-slate-500 border border-moebus-border hover:bg-slate-50"
                        }`}
                    >
                        <Globe size={16} /> Webseiten-Einstellungen
                    </button>
                </div>

                {activeTab === "jobs" && <JobManagement />}
                {activeTab === "stats" && <AnalyticsView />}

                {activeTab === "website" && (
                <>
                <div className="flex flex-wrap items-center gap-x-1 gap-y-2 mb-6 bg-white border border-moebus-border rounded-lg p-1.5 w-fit">
                    <span className="px-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Startseite</span>
                    {[
                        ["benefits", "Vorteile", Star],
                        ["about", "Wer wir sind", Users],
                        ["whatwedo", "Was wir machen", ClipboardList],
                        ["jobsintro", "Was wir suchen", Search],
                        ["hero", "Startbild", ImageIcon],
                    ].map(([key, label, Icon]) => (
                        <button
                            key={key}
                            data-testid={`settings-tab-${key}`}
                            onClick={() => setWebsiteTab(key)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                websiteTab === key ? "bg-moebus-deep text-white" : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                            <Icon size={13} /> {label}
                        </button>
                    ))}
                    <span className="w-px h-5 bg-moebus-border mx-2"></span>
                    <span className="px-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">System</span>
                    {[
                        ["legal", "Rechtstexte", FileText],
                        ["templates", "E-Mail-Vorlagen", Mail],
                        ["users", "Benutzer", Users],
                    ].map(([key, label, Icon]) => (
                        <button
                            key={key}
                            data-testid={`settings-tab-${key}`}
                            onClick={() => setWebsiteTab(key)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                websiteTab === key ? "bg-moebus-deep text-white" : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                            <Icon size={13} /> {label}
                        </button>
                    ))}
                </div>
                {websiteTab === "benefits" && <BenefitsSettings />}
                {websiteTab === "about" && <AboutEditor />}
                {websiteTab === "whatwedo" && <WhatWeDoEditor />}
                {websiteTab === "jobsintro" && <JobsIntroEditor />}
                {websiteTab === "hero" && <HeroSettings />}
                {websiteTab === "legal" && <LegalPages />}
                {websiteTab === "templates" && <EmailTemplates />}
                {websiteTab === "users" && <UserManagement />}
                </>
                )}

                {activeTab === "applications" && (
                <>
                <AdminStats refreshKey={items.length} />
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="font-heading font-bold text-2xl md:text-3xl text-moebus-deep">
                            {view === "archive" ? "Archiv – Eingestellte Bewerber" : "Bewerbungen"}
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {items.length === 1 ? "1 Eintrag" : `${items.length} Einträge`}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="test-email-btn"
                            onClick={sendTestEmail}
                            disabled={sendingTest}
                            className="inline-flex items-center gap-2 bg-white border border-moebus-border text-slate-700 px-3 py-2 rounded-md text-sm hover:bg-slate-50 disabled:opacity-60"
                        >
                            <Send size={14} /> {sendingTest ? "Sende…" : "Test-E-Mail"}
                        </button>
                        <button
                            data-testid="refresh-btn"
                            onClick={fetchItems}
                            className="inline-flex items-center gap-2 bg-white border border-moebus-border text-slate-700 px-3 py-2 rounded-md text-sm hover:bg-slate-50"
                        >
                            <RefreshCw size={14} /> Aktualisieren
                        </button>
                        <button
                            data-testid="export-csv-btn"
                            onClick={exportCsv}
                            className="inline-flex items-center gap-2 bg-moebus-deep hover:bg-slate-800 text-white px-3 py-2 rounded-md text-sm"
                        >
                            <Download size={14} /> CSV-Export
                        </button>
                    </div>
                </div>

                {/* Aktiv / Archiv */}
                <div className="inline-flex items-center gap-1 bg-white border border-moebus-border rounded-lg p-1 mb-4">
                    <button
                        data-testid="view-active"
                        onClick={() => { setView("active"); setStatusFilter("all"); }}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            view === "active" ? "bg-moebus-deep text-white" : "text-slate-600 hover:bg-slate-100"
                        }`}
                    >
                        Aktive Bewerbungen
                    </button>
                    <button
                        data-testid="view-archive"
                        onClick={() => { setView("archive"); setStatusFilter("all"); }}
                        className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            view === "archive" ? "bg-moebus-deep text-white" : "text-slate-600 hover:bg-slate-100"
                        }`}
                    >
                        <Archive size={13} /> Archiv (Eingestellt)
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white border border-moebus-border rounded-lg p-4 md:p-5 mb-6">
                    <div className="grid md:grid-cols-12 gap-3">
                        <div className="md:col-span-5 relative">
                            <Search
                                size={16}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <input
                                data-testid="admin-search"
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Suche nach Name, E-Mail, Ort, Nachricht, Notizen…"
                                className="w-full pl-10 pr-4 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green"
                            />
                        </div>
                        <div className="md:col-span-3">
                            <select
                                data-testid="admin-filter-status"
                                value={view === "archive" ? "all" : statusFilter}
                                disabled={view === "archive"}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-moebus-green disabled:opacity-50"
                            >
                                <option value="all">{view === "archive" ? "Status: Eingestellt" : "Alle Status"}</option>
                                {STATUSES.filter((s) => s !== "Eingestellt").map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-4">
                            <select
                                data-testid="admin-filter-position"
                                value={positionFilter}
                                onChange={(e) => setPositionFilter(e.target.value)}
                                className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-moebus-green"
                            >
                                <option value="all">Alle Bereiche</option>
                                {positionOptions.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white border border-moebus-border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table data-testid="applications-table" className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-moebus-border">
                                <tr className="text-left">
                                    {[
                                        ["created_at", "Eingegangen"],
                                        ["last_name", "Name"],
                                        ["position", "Bereich"],
                                        ["status", "Status"],
                                    ].map(([field, label]) => (
                                        <th
                                            key={field}
                                            className="px-4 py-3 font-medium text-slate-600 cursor-pointer hover:text-moebus-deep"
                                            onClick={() => toggleSort(field)}
                                            data-testid={`sort-${field}`}
                                        >
                                            <span className="inline-flex items-center gap-1">
                                                {label}
                                                {sortBy === field && (
                                                    <span className="text-moebus-green">
                                                        {sortDir === "asc" ? "↑" : "↓"}
                                                    </span>
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                    <th className="px-4 py-3 font-medium text-slate-600">Kontakt</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">Dateien</th>
                                    <th className="px-4 py-3 font-medium text-slate-600 text-right">Aktion</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={7} className="p-8 text-center text-slate-500">
                                            Lade…
                                        </td>
                                    </tr>
                                )}
                                {!loading && items.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="p-12 text-center text-slate-500">
                                            Keine Bewerbungen gefunden.
                                        </td>
                                    </tr>
                                )}
                                {!loading &&
                                    items.map((a) => (
                                        <tr
                                            key={a.id}
                                            data-testid={`row-${a.id}`}
                                            className={`border-b border-moebus-border last:border-0 ${
                                                isStale(a) ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-slate-50"
                                            }`}
                                        >
                                            <td className="px-4 py-3 text-slate-600">{formatDate(a.created_at)}</td>
                                            <td className="px-4 py-3 font-medium text-moebus-deep">
                                                {a.first_name} {a.last_name}
                                                <div className="text-xs text-slate-500 font-normal">{a.city}</div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">{a.position}</td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                                                        STATUS_COLORS[a.status] || "bg-slate-100 text-slate-700"
                                                    }`}
                                                >
                                                    {a.status}
                                                </span>
                                                {isStale(a) && (
                                                    <div
                                                        data-testid={`stale-badge-${a.id}`}
                                                        className="flex items-center gap-1 text-[11px] text-amber-700 font-medium mt-1.5"
                                                    >
                                                        <AlertTriangle size={12} /> über 7 Tage unbearbeitet
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-slate-600 text-xs">
                                                <div>{a.email}</div>
                                                <div>{a.phone}</div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">
                                                {a.files?.length || 0}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    data-testid={`view-${a.id}`}
                                                    onClick={() => openDetail(a.id)}
                                                    className="inline-flex items-center gap-1 text-moebus-green hover:text-emerald-700 text-sm font-medium"
                                                >
                                                    <Eye size={14} /> Ansehen
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <GdprCard />
                </>
                )}
            </main>

            {/* Detail dialog */}
            {selected && (
                <div
                    data-testid="detail-dialog"
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
                    onClick={(e) => e.target === e.currentTarget && closeDetail()}
                >
                    <div className="bg-white w-full max-w-3xl max-h-[95vh] md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-lg shadow-2xl">
                        <div className="sticky top-0 bg-white border-b border-moebus-border px-6 py-4 flex items-start justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-moebus-green font-semibold">
                                    {selected.position}
                                </div>
                                <h2 className="font-heading font-bold text-xl md:text-2xl text-moebus-deep mt-1">
                                    {selected.first_name} {selected.last_name}
                                </h2>
                            </div>
                            <button
                                data-testid="close-detail"
                                onClick={closeDetail}
                                className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
                            >
                                ×
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                <div className="flex items-start gap-2">
                                    <Mail size={16} className="text-moebus-muted mt-0.5" />
                                    <div>
                                        <div className="text-xs text-slate-500">E-Mail</div>
                                        <a
                                            href={`mailto:${selected.email}`}
                                            className="text-moebus-deep hover:text-moebus-green break-all"
                                        >
                                            {selected.email}
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Phone size={16} className="text-moebus-muted mt-0.5" />
                                    <div>
                                        <div className="text-xs text-slate-500">Telefon</div>
                                        <a
                                            href={`tel:${selected.phone}`}
                                            className="text-moebus-deep hover:text-moebus-green"
                                        >
                                            {selected.phone}
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <MapPin size={16} className="text-moebus-muted mt-0.5" />
                                    <div>
                                        <div className="text-xs text-slate-500">Wohnort</div>
                                        <div className="text-moebus-deep">{selected.city}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Calendar size={16} className="text-moebus-muted mt-0.5" />
                                    <div>
                                        <div className="text-xs text-slate-500">Eingegangen</div>
                                        <div className="text-moebus-deep">{formatDate(selected.created_at)}</div>
                                    </div>
                                </div>
                            </div>

                            {selected.message && (
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-2">
                                        Nachricht des Bewerbers
                                    </div>
                                    <div className="bg-slate-50 border border-moebus-border rounded-md p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                        {selected.message}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-2">
                                    Hochgeladene Dateien ({selected.files?.length || 0})
                                </div>
                                {(!selected.files || selected.files.length === 0) && (
                                    <div className="text-sm text-slate-500">Keine Dateien</div>
                                )}
                                <ul className="space-y-2">
                                    {(selected.files || []).map((f) => {
                                        const ext = f.original_filename?.split(".").pop().toLowerCase();
                                        const Icon = ["jpg", "jpeg", "png"].includes(ext) ? ImageIcon : FileText;
                                        return (
                                            <li
                                                key={f.id}
                                                className="flex items-center gap-3 bg-white border border-moebus-border rounded-md p-3"
                                            >
                                                <Icon size={20} className="text-moebus-blue" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-moebus-deep truncate">
                                                        {f.original_filename}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {Math.round((f.size || 0) / 1024)} KB
                                                    </div>
                                                </div>
                                                <button
                                                    data-testid={`download-${f.id}`}
                                                    onClick={() =>
                                                        downloadFile(selected.id, f.id, f.original_filename)
                                                    }
                                                    className="inline-flex items-center gap-1 text-sm text-moebus-green hover:text-emerald-700 font-medium"
                                                >
                                                    <Download size={14} /> Download
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            {(selected.email_status || (selected.status_emails || []).length > 0 || (selected.sent_emails || []).length > 0) && (
                                <div data-testid="email-history">
                                    <div className="text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-2">
                                        Gesendete E-Mails
                                    </div>
                                    <ul className="space-y-1.5">
                                        {selected.email_status && (
                                            <li className="flex items-center gap-2 text-sm text-slate-700">
                                                {selected.email_status.applicant?.ok ? (
                                                    <CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0" />
                                                ) : (
                                                    <XCircle size={15} className="text-red-500 flex-shrink-0" />
                                                )}
                                                <span>
                                                    Eingangsbestätigung an Bewerber
                                                    <span className="text-xs text-slate-400 ml-2">
                                                        {formatDate(selected.email_status.sent_at)}
                                                    </span>
                                                </span>
                                            </li>
                                        )}
                                        {(selected.status_emails || []).map((e, i) => (
                                            <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                                                {e.result?.ok ? (
                                                    <CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0" />
                                                ) : (
                                                    <XCircle size={15} className="text-red-500 flex-shrink-0" />
                                                )}
                                                <span>
                                                    Status-Mail „{e.status}"
                                                    <span className="text-xs text-slate-400 ml-2">{formatDate(e.at)}</span>
                                                    {!e.result?.ok && (
                                                        <span className="text-xs text-red-500 ml-2">fehlgeschlagen</span>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                        {(selected.sent_emails || []).map((e) => (
                                            <li key={e.id} className="flex items-start gap-2 text-sm text-slate-700" data-testid="sent-email-entry">
                                                {e.result?.ok ? (
                                                    <CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                                                ) : (
                                                    <XCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                                                )}
                                                <span>
                                                    Individuelle E-Mail: „{e.subject}"
                                                    <span className="text-xs text-slate-400 ml-2">
                                                        {formatDate(e.sent_at)} · von {e.sent_by}
                                                    </span>
                                                    {!e.result?.ok && (
                                                        <span className="text-xs text-red-500 ml-2">fehlgeschlagen</span>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex flex-wrap items-center gap-2">
                                <SendEmailDialog
                                    application={selected}
                                    onSent={(entry) => {
                                        setSelected({ ...selected, sent_emails: [...(selected.sent_emails || []), entry] });
                                        fetchItems();
                                    }}
                                />
                                <button
                                    data-testid="send-manage-link-btn"
                                    onClick={async () => {
                                        try {
                                            const { data } = await apiClient.post(`/applications/${selected.id}/send-manage-link`);
                                            if (data.ok) {
                                                toast.success(`Persönlicher Link an ${selected.email} gesendet.`);
                                            } else {
                                                toast.error(`Senden fehlgeschlagen: ${data.error || "Unbekannter Fehler"}`, { duration: 10000 });
                                            }
                                            setSelected({ ...selected, sent_emails: [...(selected.sent_emails || []), data.email] });
                                            fetchItems();
                                        } catch (e) {
                                            toast.error(formatApiError(e?.response?.data?.detail) || "Senden fehlgeschlagen.");
                                        }
                                    }}
                                    className="inline-flex items-center gap-2 text-sm font-medium text-moebus-deep bg-white border border-moebus-border hover:bg-slate-50 px-4 py-2 rounded-md"
                                >
                                    <LinkIcon size={14} /> Persönlichen Link senden
                                </button>
                                {selected.status === "Zurückgezogen" && (
                                    <button
                                        data-testid="reactivate-btn"
                                        onClick={async () => {
                                            if (!window.confirm(`Rückzug von ${selected.first_name} ${selected.last_name} rückgängig machen? Die Bewerbung wird wieder aktiv (Status „Neu“) und der Bewerber erhält eine E-Mail mit neuem persönlichen Link.`)) return;
                                            try {
                                                const { data } = await apiClient.post(`/applications/${selected.id}/reactivate`);
                                                if (data.ok) {
                                                    toast.success("Rückzug rückgängig gemacht – Bewerber wurde per E-Mail informiert.");
                                                } else {
                                                    toast.error(`Reaktiviert, aber E-Mail fehlgeschlagen: ${data.error || "Unbekannter Fehler"}`, { duration: 10000 });
                                                }
                                                setSelected(data.application);
                                                fetchItems();
                                            } catch (e) {
                                                toast.error(formatApiError(e?.response?.data?.detail) || "Reaktivierung fehlgeschlagen.");
                                            }
                                        }}
                                        className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-4 py-2 rounded-md"
                                    >
                                        <RotateCcw size={14} /> Rückzug rückgängig machen
                                    </button>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-2">
                                    Status
                                </label>
                                <select
                                    data-testid="status-select"
                                    value={statusEdit}
                                    onChange={(e) => setStatusEdit(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-moebus-green"
                                >
                                    {STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <label className="flex items-center gap-2.5 mt-3 cursor-pointer select-none">
                                    <input
                                        data-testid="notify-applicant-checkbox"
                                        type="checkbox"
                                        checked={notifyApplicant}
                                        onChange={(e) => setNotifyApplicant(e.target.checked)}
                                        className="w-4 h-4 accent-emerald-600"
                                    />
                                    <span className="text-sm text-slate-700">
                                        Bewerber per E-Mail über den Status benachrichtigen
                                    </span>
                                </label>
                                {notifyApplicant && (
                                    <p className="text-xs text-slate-500 mt-1.5 ml-6">
                                        Beim Speichern wird die E-Mail-Vorlage für „{statusEdit}" an {selected.email} gesendet.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-2">
                                    Interne Notizen
                                </label>
                                <textarea
                                    data-testid="notes-textarea"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={4}
                                    className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green resize-y"
                                    placeholder="Interne Vermerke (nur für Admin sichtbar)…"
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                <button
                                    data-testid="save-detail"
                                    onClick={saveDetail}
                                    disabled={savingDetail}
                                    className="inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-5 py-2.5 rounded-md text-sm flex-1"
                                >
                                    <Save size={14} /> {savingDetail ? "Speichern…" : "Speichern"}
                                </button>
                                <button
                                    data-testid="export-pdf-btn"
                                    onClick={exportPdf}
                                    className="inline-flex items-center justify-center gap-2 bg-white border border-moebus-border hover:bg-slate-50 text-slate-700 font-medium px-5 py-2.5 rounded-md text-sm"
                                >
                                    <FileDown size={14} /> PDF
                                </button>
                                <button
                                    data-testid="delete-detail"
                                    onClick={() => deleteApp(selected.id)}
                                    className="inline-flex items-center justify-center gap-2 bg-white border border-red-200 hover:bg-red-50 text-red-700 font-medium px-5 py-2.5 rounded-md text-sm"
                                >
                                    <Trash2 size={14} /> Löschen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Password change dialog */}
            {showPwDialog && (
                <div
                    data-testid="password-dialog"
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowPwDialog(false)}
                >
                    <div className="bg-white w-full max-w-md rounded-lg shadow-2xl p-6">
                        <h2 className="font-heading font-bold text-xl text-moebus-deep mb-4">
                            Passwort ändern
                        </h2>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Aktuelles Passwort
                                </label>
                                <input
                                    data-testid="pw-current"
                                    type="password"
                                    value={pwForm.current}
                                    onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Neues Passwort (mind. 8 Zeichen)
                                </label>
                                <input
                                    data-testid="pw-new"
                                    type="password"
                                    value={pwForm.next}
                                    onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Neues Passwort wiederholen
                                </label>
                                <input
                                    data-testid="pw-confirm"
                                    type="password"
                                    value={pwForm.confirm}
                                    onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-5">
                            <button
                                data-testid="pw-save"
                                onClick={changePassword}
                                disabled={changingPw}
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-5 py-2.5 rounded-md text-sm disabled:opacity-60"
                            >
                                <KeyRound size={14} /> {changingPw ? "Ändern…" : "Passwort ändern"}
                            </button>
                            <button
                                onClick={() => setShowPwDialog(false)}
                                className="inline-flex items-center justify-center bg-white border border-moebus-border text-slate-700 px-5 py-2.5 rounded-md text-sm hover:bg-slate-50"
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
